import {I18n} from '@lingui/core';
import {useLingui} from '@lingui/react';
import {createColumnHelper} from '@tanstack/react-table';
import {ChargebackListItemOutput} from '@zentact/api/src/trpc/routers/chargebackRouter';
import {
  ChargebackPublicStatus,
  CurrencyCode,
  LocaleCode,
  TableColumnSize,
  formatAmount,
  formatLocaleDate,
} from '@zentact/common';
import type {ChargebackDisputeStatus, ChargebackType} from '@zentact/db';
import {DateTime} from 'luxon';
import {Link, generatePath} from 'react-router-dom';
import {DropDownMinimalMenuIcon} from '../../../dropdowns';
import {FlatFillColors, FlatPillWithDot} from '../../../elements';
import {TruncatedText} from '../../../other';
import {getTableMeta} from '../../../table';
import {CellCollapsedData} from '../../../table/cells/cell-collapsed-data/cell-collapsed-data';
import {renderPaymentMethod} from '../payment-list/columns';

export const displayChargebackTypeMap: {
  [_ in ChargebackType]: string;
} = {
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  NOTIFICATION_OF_FRAUD: 'Notification Of Fraud',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  REQUEST_FOR_INFORMATION: 'Request For Information',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  CHARGEBACK: 'Chargeback',
};

export const getChargebackDisputeStatusMap = (i18n: I18n) =>
  ({
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    NOT_APPLICABLE: i18n._('Not applicable'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    UNRESPONDED: i18n._('Not Responded'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    RESPONDED: i18n._('Responded'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    EXPIRED: i18n._('Expired'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    UNDEFENDED: i18n._('Not Defended'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    PENDING: i18n._('Pending'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    ACCEPTED: i18n._('Accepted'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    LOST: i18n._('Lost'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    WON: i18n._('Won'),
  }) satisfies Record<ChargebackDisputeStatus, string>;

export const getChargebackStatusMap = (i18n: I18n) =>
  ({
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    NOTIFICATION_OF_FRAUD_NEEDS_REVIEW: i18n._('Notification of Fraud - Needs Review'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    REQUEST_FOR_INFO_NEEDS_REVIEW: i18n._('Request for Info - Needs Review'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    REQUEST_FOR_INFO_RESPONDED: i18n._('Request for Info - Responded'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    REQUEST_FOR_INFO_EXPIRED: i18n._('Request for Info - Expired'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    NOTIFICATION_OF_CHARGEBACK_UNDEFENDED: i18n._('Notification of Chargeback - Needs Defense'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    NOTIFICATION_OF_CHARGEBACK_PENDING: i18n._('Notification of Chargeback - Pending'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    CHARGEBACK_UNDEFENDED: i18n._('Chargeback - Needs Defense'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    CHARGEBACK_PENDING: i18n._('Chargeback - Pending'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    CHARGEBACK_ACCEPTED: i18n._('Chargeback - Accepted'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    CHARGEBACK_LOST: i18n._('Chargeback - Lost'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    SECOND_CHARGEBACK_LOST: i18n._('Second Chargeback - Lost'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    CHARGEBACK_REVERSED_WON: i18n._('Chargeback Reversed - Won'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    CHARGEBACK_REVERSED_PENDING: i18n._('Chargeback Reversed - Pending'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    PREARBITRATION_WON: i18n._('Prearbitration - Won'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    PREARBITRATION_LOST: i18n._('Prearbitration - Lost'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    DEFENSE_PERIOD_ENDED_UNDEFENDED: i18n._('Defense Period Ended - Undefended'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    DEFENSE_PERIOD_ENDED_LOST: i18n._('Defense Period Ended - Lost'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    DEFENSE_PERIOD_ENDED_ACCEPTED: i18n._('Defense Period Ended - Accepted'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    INFORMATION_SUPPLIED_RESPONDED: i18n._('Information Supplied - Responded'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    INFORMATION_SUPPLIED_PENDING: i18n._('Information Supplied - Pending'),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    ISSUER_DEFENSE_ENDED_WON: i18n._('Issuer Defense Ended - Won'),
  }) satisfies Record<ChargebackPublicStatus, string>;

export const chargebackStatusToColor: {
  [_ in ChargebackPublicStatus]: FlatFillColors;
} = {
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  NOTIFICATION_OF_FRAUD_NEEDS_REVIEW: 'red',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  REQUEST_FOR_INFO_NEEDS_REVIEW: 'red',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  REQUEST_FOR_INFO_RESPONDED: 'yellow',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  REQUEST_FOR_INFO_EXPIRED: 'gray',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  NOTIFICATION_OF_CHARGEBACK_UNDEFENDED: 'red',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  NOTIFICATION_OF_CHARGEBACK_PENDING: 'yellow',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  CHARGEBACK_UNDEFENDED: 'red',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  CHARGEBACK_PENDING: 'yellow',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  CHARGEBACK_ACCEPTED: 'gray',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  CHARGEBACK_LOST: 'gray',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  SECOND_CHARGEBACK_LOST: 'gray',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  CHARGEBACK_REVERSED_WON: 'green',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  CHARGEBACK_REVERSED_PENDING: 'yellow',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  PREARBITRATION_WON: 'green',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  PREARBITRATION_LOST: 'gray',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  DEFENSE_PERIOD_ENDED_UNDEFENDED: 'gray',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  DEFENSE_PERIOD_ENDED_LOST: 'gray',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  DEFENSE_PERIOD_ENDED_ACCEPTED: 'gray',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  INFORMATION_SUPPLIED_RESPONDED: 'yellow',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  INFORMATION_SUPPLIED_PENDING: 'yellow',
  // biome-ignore lint/style/useNamingConvention: DB Type conversion
  ISSUER_DEFENSE_ENDED_WON: 'green',
};

export const getChargebackStatusDescriptionMap = (i18n: I18n) =>
  ({
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    NOTIFICATION_OF_FRAUD_NEEDS_REVIEW: i18n._(
      `An informational alert about potential fraud activity (equivalent to Visa TC40 or Mastercard SAFE report). No money has been withdrawn from your account, but this transaction could become a chargeback if there's no 3D Secure liability shift. Review this alert promptly and consider proactively issuing a refund, blocking the shopper, or stopping shipment to avoid a potential future chargeback.`
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    REQUEST_FOR_INFO_NEEDS_REVIEW: i18n._(
      'The issuing bank is requesting more information about this transaction. No money has been withdrawn yet, but failure to respond within the required timeframe may result in a chargeback. Provide the requested transaction information as soon as possible.'
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    REQUEST_FOR_INFO_RESPONDED: i18n._(
      `You've responded to the information request and are awaiting the issuer's decision. The issuer is reviewing your submitted information.`
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    REQUEST_FOR_INFO_EXPIRED: i18n._(
      'You did not respond to the information request within the required timeframe. This may lead to a chargeback.'
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    NOTIFICATION_OF_CHARGEBACK_UNDEFENDED: i18n._(
      'The issuing bank has initiated a chargeback on behalf of the cardholder. This is your starting point for defense, marking the beginning of the defense period. Money will be withdrawn from your account shortly. Evaluate the chargeback details and prepare defense documents if you have valid evidence to contest it.'
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    NOTIFICATION_OF_CHARGEBACK_PENDING: i18n._(
      'The chargeback notification has been received and is awaiting processing.'
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    CHARGEBACK_UNDEFENDED: i18n._(
      'A payment has been charged back by the issuing bank. The disputed amount has been debited from your account. Review the chargeback reason and upload defense documents within the specified timeframe if you have compelling evidence to dispute this claim.'
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    CHARGEBACK_PENDING: i18n._(
      `You've submitted defense documents for this chargeback and are waiting for the issuer's review.`
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    CHARGEBACK_ACCEPTED: i18n._(
      `You've accepted the chargeback as valid. The disputed amount will not be returned to your account.`
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    CHARGEBACK_LOST: i18n._(
      `The chargeback has been finalized and you've lost the dispute. The funds will not be returned to your account.`
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    SECOND_CHARGEBACK_LOST: i18n._(
      'The issuing bank has declined your defense materials for the original chargeback and initiated a second chargeback. This is a final status - you cannot submit additional defense documents and the disputed amount will be debited from your account again.'
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    CHARGEBACK_REVERSED_WON: i18n._(
      'The chargeback has been reversed in your favor. The disputed amount has been returned to your account and this is the final status of the dispute.'
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    CHARGEBACK_REVERSED_PENDING: i18n._(
      'The disputed amount has been transferred back to your account while the issuing bank reviews your defense. This is not final - the issuer is still evaluating your evidence and may challenge it.'
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    PREARBITRATION_WON: i18n._(
      `The pre-arbitration case initiated by the cardholder's bank has been decided in your favor. The disputed amount remains in your account. This status only applies to disputes that included a pre-arbitration stage.`
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    PREARBITRATION_LOST: i18n._(
      `The pre-arbitration case initiated by the cardholder's bank has been decided against you. The disputed amount will be debited from your account. For certain payment networks, this may lead to a second chargeback.`
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    DEFENSE_PERIOD_ENDED_UNDEFENDED: i18n._(
      'You did not defend the dispute within the required timeframe, or the defense period expired because you accepted liability. You can no longer submit defense documents for this dispute.'
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    DEFENSE_PERIOD_ENDED_LOST: i18n._(
      'You defended the dispute but lost. The disputed amount will not be returned to your account.'
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    DEFENSE_PERIOD_ENDED_ACCEPTED: i18n._(
      `You've accepted the dispute outcome. The disputed amount will not be returned to your account.`
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    INFORMATION_SUPPLIED_RESPONDED: i18n._(
      `Adyen has automatically defended the dispute on your behalf (without you needing to upload documents). The defense has been forwarded to the payment scheme and cannot be changed. You're now waiting for the issuer's decision.`
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    INFORMATION_SUPPLIED_PENDING: i18n._(
      `You've successfully uploaded your dispute defense documents. These documents have been forwarded to the payment scheme and cannot be changed. You're now waiting for the issuer's decision.`
    ),
    // biome-ignore lint/style/useNamingConvention: DB Type conversion
    ISSUER_DEFENSE_ENDED_WON: i18n._(
      `The issuer either accepted your defense or did not respond within the required timeframe. You've won the dispute and the case is now closed in your favor.`
    ),
  }) satisfies Record<ChargebackPublicStatus, string>;

const columnsHelper = createColumnHelper<ChargebackListItemOutput>();

type Props = {
  locale: LocaleCode;
  i18n: I18n;
  openDetailsPanel: (row: ChargebackListItemOutput) => void;
  paymentDetailsPath: string;
  showOrganizationColumn?: boolean;
};

export const getChargebackColumns = ({
  locale,
  i18n,
  openDetailsPanel,
  showOrganizationColumn,
  paymentDetailsPath,
}: Props) => {
  return [
    // @ts-ignore fix TS2589: Type instantiation is excessively deep and possibly infinite. error
    columnsHelper.accessor('createdAt', {
      cell: props => formatLocaleDate(props.getValue(), 'shortWithTime'),
      header: () => i18n._('Dispute Date'),
      meta: {
        sort: {
          isSortable: true,
        },
      },
      size: TableColumnSize.S,
    }),
    columnsHelper.accessor('payment.createdAt', {
      id: 'paymentCreatedAt',
      cell: props => formatLocaleDate(props.getValue(), 'shortWithTime'),
      header: () => i18n._('Payment Date'),
      meta: {
        sort: {
          isSortable: true,
        },
        visibleAt: 'md',
      },
      size: TableColumnSize.S,
    }),
    columnsHelper.display({
      cell: props => <TruncatedText text={props.row.original.reason} />,
      id: 'reason',
      header: () => i18n._('Dispute Reason'),
      meta: {
        visibleAt: 'md',
      },
      size: TableColumnSize.L,
    }),
    columnsHelper.display({
      id: 'defendable',
      header: () => i18n._('Defendable'),
      cell: props => {
        const {breakpoints} = getTableMeta(props.table);
        const defendable = props.row.original.defendable;
        const daysLeft =
          props.row.original.defendable && props.row.original.defensePeriodEndsAt
            ? Math.floor(
                DateTime.fromISO(props.row.original.defensePeriodEndsAt).diff(
                  DateTime.now(),
                  'days'
                ).days
              )
            : 0;
        return (
          <div className="flex flex-col justify-center">
            <span className="font-semibold">
              {!breakpoints.lg ? i18n._('Defendable:') : ''}{' '}
              {defendable ? i18n._('Yes') : i18n._('No')}
            </span>
            {defendable && (
              <span className="mt-1">
                {i18n._(
                  '{count} days left',
                  {count: daysLeft > 0 ? daysLeft : 0},
                  {
                    message: '{count, plural, =1 {{count} day left} other {{count} days left}}',
                  }
                )}
              </span>
            )}
          </div>
        );
      },
      meta: {
        collapseAt: 'lg',
      },
    }),
    columnsHelper.display({
      cell: props => {
        const pspReferenceId = props.row.original.payment.pspReferenceId || '';

        return (
          <Link
            className="text-sm text-primary-600"
            to={generatePath(paymentDetailsPath, {
              pspReferenceId,
            })}
          >
            {pspReferenceId}
          </Link>
        );
      },
      id: 'paymentPspReferenceId',
      header: () => i18n._('Payment ID'),
      meta: {
        visibleAt: '2xl',
      },
      size: TableColumnSize.L,
    }),
    columnsHelper.display({
      id: 'merchantName',
      header: () => i18n._('Merchant Account'),
      cell: props => {
        const {breakpoints} = getTableMeta(props.table);
        const merchantName = props.row.original.merchantAccount.businessName;
        const organizationName = props.row.original.organization?.name || 'N/A';

        return (
          <CellCollapsedData
            primary={<TruncatedText text={merchantName} />}
            secondary={<TruncatedText text={organizationName} />}
            hideSecondary={!(showOrganizationColumn && breakpoints.sm)}
          />
        );
      },
      meta: {
        collapseAt: 'sm',
      },
    }),
    columnsHelper.accessor(row => formatAmount(row.amount, locale, row.currency as CurrencyCode), {
      id: 'amount',
      header: () => i18n._('Amount'),
      meta: {
        sort: {
          isSortable: true,
        },
        visibleAt: '2xl',
        align: 'right',
      },
      size: TableColumnSize.XS,
    }),
    columnsHelper.display({
      cell: props =>
        renderPaymentMethod(
          props.row.original.paymentMethod,
          props.row.original.payment.cardSummary
        ),
      id: 'paymentMethod',
      header: () => i18n._('Payment Method'),
      meta: {
        collapseAt: 'full',
        visibleAt: '2xl',
        align: 'right',
      },
      size: 140,
    }),
    columnsHelper.display({
      id: 'status',
      header: () => i18n._('Status'),
      cell: props => {
        const {status} = props.row.original;

        return (
          <FlatPillWithDot
            color={chargebackStatusToColor[status] || 'blue'}
            label={getChargebackStatusMap(i18n)[status] ?? status}
          />
        );
      },

      meta: {
        align: 'right',
        collapseAt: 'sm',
        headerQuestionModal: {
          content: <ChargebackStatusTooltip />,
          localeText: {
            title: i18n._('Dispute Status Description'),
            okay: i18n._('Close'),
          },
        },
      },
      size: 180,
    }),
    columnsHelper.display({
      id: 'actions',
      cell: props => (
        <div className="flex justify-center">
          <DropDownMinimalMenuIcon
            items={[
              {name: i18n._('View Details'), onClick: () => openDetailsPanel(props.row.original)},
            ]}
          />
        </div>
      ),
      size: TableColumnSize.ACTIONS,
    }),
  ];
};

const ChargebackStatusTooltip = () => {
  const {i18n} = useLingui();
  const statusMap = getChargebackStatusMap(i18n);
  const descriptionMap = getChargebackStatusDescriptionMap(i18n);
  return (
    <div>
      {Object.values(ChargebackPublicStatus).map(status => (
        <div key={status} className="py-1">
          <FlatPillWithDot
            color={chargebackStatusToColor[status]}
            label={statusMap[status]}
            className="mr-1"
          />
          {descriptionMap[status]}
        </div>
      ))}
    </div>
  );
};
